import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { btnGhost } from '../ui/styles';
import { fetchHandoffNotes, markHandoffNoteRead } from '../adminApi';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { errorMessage } from '../../core/errors';
import { dateOf, dayMedium } from '../../core/format/dates';
import { ADMIN_BASE } from '../../utils/hosts';
import type { HandoffNote } from '../../core/types/handoff';

/**
 * Op Sarahs overzicht: ongelezen notities van de collega, per klant. Blijven staan tot "Gelezen".
 * Niets ongelezen: het paneel verdwijnt (geen lege doos op het overzicht).
 */
export function HandoffNotesPanel() {
  const load = useCallback((opts: {
    signal?: AbortSignal;
  }) => fetchHandoffNotes(opts), []);
  const {
    data,
    setData
  } = useLatestLoad<HandoffNote[]>(load);
  const [busy, setBusy] = useState<string | null>(null);
  if (!data || data.length === 0) return null;
  const markRead = async (n: HandoffNote) => {
    setBusy(n.id);
    try {
      await markHandoffNoteRead(n.id);
      setData(list => (list ?? []).filter(x => x.id !== n.id));
    } catch (err) {
      toast.error('Markeren lukte niet', {
        description: errorMessage(err)
      });
    } finally {
      setBusy(null);
    }
  };
  return <Panel title="Notities van je collega" description={`${data.length} ongelezen`} flush data-id="element-1637" data-mp="2ly3vb91h8c6k">
      <ul className="divide-y divide-line" data-id="element-1638" data-mp="3inv144f7xlro">
        {data.map(n => <li key={n.id} className="px-4 py-3 sm:px-5" data-id="element-1639" data-mp="3ep3zpl3pzhyr">
            <div className="flex items-baseline justify-between gap-3" data-id="element-1640" data-mp="39oztqp0fu5ms">
              <Link to={`${ADMIN_BASE}/klanten/${n.customerId}`} className="truncate text-[14px] text-ink underline-offset-4 hover:underline" data-id="element-1641" data-mp="39rcbb12aadzb">
                {n.customerName}
              </Link>
              <span className="shrink-0 text-[12px] text-muted" data-id="element-1642" data-mp="1y7ztn77ummi7">
                {n.fromName} · {dayMedium(dateOf(n.createdAt))}
              </span>
            </div>
            <p className="mt-1 whitespace-pre-line text-[13px] leading-relaxed text-ink" data-id="element-1643" data-mp="1usyajflg8bth">{n.text}</p>
            <button type="button" onClick={() => void markRead(n)} disabled={busy === n.id} aria-busy={busy === n.id} className={`${btnGhost} -ml-2 mt-1`} data-id="element-1644" data-mp="1zqzstyb1w73w">
              <CheckIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1645" data-mp="1qqbo5u7jrj3o" />
              Gelezen
            </button>
          </li>)}
      </ul>
    </Panel>;
}